import { Panel } from "@/components/admin/ui";
import { COUNTRIES, STATES_BY_COUNTRY, hasStateList } from "@/lib/geoData";
import { ShieldAlert, ShieldQuestion } from "lucide-react";

const inputClass = "mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm dark:border-white/15 dark:bg-white/5 dark:text-white";
const labelClass = "text-xs font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500";

// Transient result of the last "Verify Address" click only - the persistent verification
// state (Not Verified / Verified / Map Identified) is shown on the Location Preview card.
const VerifyResultChip = ({ verifyStatus }) => {
  if (!verifyStatus) return null;
  if (verifyStatus.configured === false) {
    return <span data-testid="org-settings-address-status" className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-500 dark:bg-white/5 dark:text-slate-400"><ShieldQuestion size={13} /> Verification not configured</span>;
  }
  if (verifyStatus.verified) return null;
  return <span data-testid="org-settings-address-status" className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-600 dark:bg-amber-500/10 dark:text-amber-400"><ShieldAlert size={13} /> Could not verify — check the address and try again</span>;
};

export const LocationSection = ({ form, onAddressChange, onVerify, verifying, verifyStatus }) => {
  const country = form.address.country || "";
  const statesForCountry = STATES_BY_COUNTRY[country];

  return (
    <Panel title="Business Location" subtitle="Your organization's primary business address." testId="org-settings-location">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className={labelClass}>Address Line 1</label>
          <input
            value={form.address.street || ""}
            onChange={(e) => onAddressChange({ street: e.target.value })}
            data-testid="org-settings-street-input"
            className={inputClass}
          />
        </div>
        <div className="sm:col-span-2">
          <label className={labelClass}>Address Line 2</label>
          <input value={form.address.line2 || ""} onChange={(e) => onAddressChange({ line2: e.target.value })} data-testid="org-settings-line2-input" className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>City</label>
          <input value={form.address.city || ""} onChange={(e) => onAddressChange({ city: e.target.value })} data-testid="org-settings-city-input" className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>State / Province</label>
          {statesForCountry ? (
            <select value={form.address.state || ""} onChange={(e) => onAddressChange({ state: e.target.value })} data-testid="org-settings-state-select" className={`${inputClass} font-semibold`}>
              <option value="">Select…</option>
              {statesForCountry.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          ) : (
            <input value={form.address.state || ""} onChange={(e) => onAddressChange({ state: e.target.value })} data-testid="org-settings-state-input" className={inputClass} />
          )}
        </div>
        <div>
          <label className={labelClass}>Postal Code</label>
          <input value={form.address.zip || ""} onChange={(e) => onAddressChange({ zip: e.target.value })} data-testid="org-settings-zip-input" className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Country</label>
          <select value={country} onChange={(e) => onAddressChange({ state: "", country: e.target.value })} data-testid="org-settings-country-select" className={`${inputClass} font-semibold`}>
            <option value="">Select…</option>
            {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>
        </div>
      </div>
      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 p-4 dark:border-white/10">
        <VerifyResultChip verifyStatus={verifyStatus} />
        <button
          type="button"
          onClick={onVerify}
          disabled={verifying}
          data-testid="org-settings-verify-address-btn"
          className="ml-auto inline-flex items-center gap-2 rounded-xl border border-slate-300 px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10"
        >
          {verifying ? "Verifying…" : "Verify Address"}
        </button>
      </div>
    </Panel>
  );
};
